'use client'

/**
 * Personas inside the app (plans/00 B5, 13.3; plans/03 A3; plans/07 section 16).
 *
 * One Keycloak login; the persona says whose view this window shows. It is
 * kept per window in sessionStorage, so a stage window and the Presenter can
 * hold different personas side by side. A new tab starts from the last persona
 * chosen anywhere (localStorage). The API client reads the persona at request
 * time and sends it as `X-AIO-Persona`.
 *
 * A page can pin a persona for its own requests with `usePersonaOverride`
 * (the Presenter always works as `supervity-support`). While an override is
 * set, the rail, the page guards and every request use it.
 */

import { useEffect, useLayoutEffect, useState, useSyncExternalStore } from 'react'

export type PersonaId =
  | 'hq-chief'
  | 'supplies-desk'
  | 'hq-secretariat'
  | 'liaison-staff'
  | 'kantei-pr'
  | 'policy-owner'
  | 'supervity-support'

export interface Persona {
  id: PersonaId
  /** Full name, shown in the "Viewing as" select. */
  name: string
  /** Short name, shown in the rail foot and in toasts. */
  short: string
  initials: string
}

/** Header name for the persona on every API call (plans/00 5.5). */
export const PERSONA_HEADER = 'X-AIO-Persona'

/** Storage key: sessionStorage per window, localStorage as the default for new tabs. */
export const PERSONA_STORAGE_KEY = 'aio.persona'

export const DEFAULT_PERSONA: PersonaId = 'hq-secretariat'

/**
 * The seven personas (artifact `40-data.js` roles). `GET /api/dr/personas`
 * (Settings `personas`) may rename them; ids never change.
 */
export const PERSONAS: readonly Persona[] = [
  // The same names as the seeded Settings `personas`, so the first paint and the loaded roster read alike.
  { id: 'hq-chief', name: 'Response head', short: 'Response head', initials: 'RH' },
  { id: 'supplies-desk', name: 'Supplies desk', short: 'Supplies desk', initials: 'SD' },
  { id: 'hq-secretariat', name: 'HQ secretariat', short: 'HQ secretariat', initials: 'HS' },
  { id: 'liaison-staff', name: 'Prefecture Liaison staff', short: 'Liaison staff', initials: 'PL' },
  { id: 'kantei-pr', name: 'Kantei PR', short: 'Kantei PR', initials: 'KP' },
  { id: 'policy-owner', name: 'Policy owner', short: 'Policy owner', initials: 'PO' },
  { id: 'supervity-support', name: 'Supervity support', short: 'Supervity support', initials: 'SS' },
]

const PERSONA_IDS = new Set<string>(PERSONAS.map((p) => p.id))

export function isPersonaId(value: unknown): value is PersonaId {
  return typeof value === 'string' && PERSONA_IDS.has(value)
}

// ─── Store ──────────────────────────────────────────────────────────────────

let chosen: PersonaId | null = null
let override: PersonaId | null = null
let roster: readonly Persona[] = PERSONAS
let version = 0
const listeners = new Set<() => void>()

function emit() {
  version += 1
  listeners.forEach((listener) => listener())
}

function readStored(): PersonaId {
  if (typeof window === 'undefined') return DEFAULT_PERSONA
  try {
    const own = window.sessionStorage.getItem(PERSONA_STORAGE_KEY)
    if (isPersonaId(own)) return own
  } catch {
    // Storage blocked: fall through to the shared default.
  }
  try {
    const shared = window.localStorage.getItem(PERSONA_STORAGE_KEY)
    if (isPersonaId(shared)) return shared
  } catch {
    // Storage blocked: use the default persona.
  }
  return DEFAULT_PERSONA
}

/** The persona this window has chosen (ignores any override). */
export function getChosenPersona(): PersonaId {
  if (chosen === null) chosen = readStored()
  return chosen
}

/** The persona the app acts as right now: the override when a page set one, else the window's choice. */
export function getPersona(): PersonaId {
  return override ?? getChosenPersona()
}

/** Sets this window's persona; new tabs start from it too. */
export function setPersona(id: PersonaId): void {
  if (!isPersonaId(id)) return
  chosen = id
  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.setItem(PERSONA_STORAGE_KEY, id)
    } catch {
      // Storage blocked: the choice still holds for this page load.
    }
    try {
      window.localStorage.setItem(PERSONA_STORAGE_KEY, id)
    } catch {
      // Storage blocked: new tabs fall back to the default persona.
    }
  }
  emit()
}

/** Pins a persona for every request and guard in this window; `null` clears it. */
export function setPersonaOverride(id: PersonaId | null): void {
  if (override === id) return
  override = id
  emit()
}

export function getPersonaOverride(): PersonaId | null {
  return override
}

/** Replaces the display names from `GET /api/dr/personas`; unknown ids are ignored. */
export function setPersonaRoster(list: readonly Partial<Persona>[] | null | undefined): void {
  if (!list || list.length === 0) return
  const byId = new Map(list.filter((p) => isPersonaId(p.id)).map((p) => [p.id as PersonaId, p]))
  roster = PERSONAS.map((base) => {
    const next = byId.get(base.id)
    return next
      ? { id: base.id, name: next.name || base.name, short: next.short || base.short, initials: next.initials || base.initials }
      : base
  })
  emit()
}

export function personaById(id: PersonaId): Persona {
  return roster.find((p) => p.id === id) ?? PERSONAS.find((p) => p.id === id) ?? PERSONAS[2]
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const getVersion = () => version
const getServerVersion = () => 0

// ─── Hooks ──────────────────────────────────────────────────────────────────

export interface PersonaState {
  /** The persona the window acts as (override first). */
  persona: PersonaId
  /** The persona chosen in the "Viewing as" select. */
  chosen: PersonaId
  /** True while a page pins the persona (Presenter). */
  pinned: boolean
  setPersona: (id: PersonaId) => void
  personas: readonly Persona[]
  current: Persona
  /** False on the server render and the first client render, before the stored persona is read. */
  hydrated: boolean
}

/**
 * The window's persona. Before hydration this returns the default persona so
 * server and client render the same markup; the stored value applies right after.
 */
export function usePersona(): PersonaState {
  useSyncExternalStore(subscribe, getVersion, getServerVersion)
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => setHydrated(true), [])
  const persona = hydrated ? getPersona() : DEFAULT_PERSONA
  const chosenId = hydrated ? getChosenPersona() : DEFAULT_PERSONA
  // Names from `GET /api/dr/personas` may arrive before a later part of the page hydrates: until this hook has hydrated it
  // renders the built-in names, as the server did, so the first client render never differs (React error #418, A2-08).
  const names = hydrated ? roster : PERSONAS
  return {
    persona,
    chosen: chosenId,
    pinned: hydrated && override !== null,
    setPersona,
    personas: names,
    current: names.find((p) => p.id === persona) ?? personaById(persona),
    hydrated,
  }
}

const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

/** Pins `id` for this window while the calling component is mounted (Presenter: `supervity-support`). */
export function usePersonaOverride(id: PersonaId): void {
  useIsoLayoutEffect(() => {
    setPersonaOverride(id)
    return () => setPersonaOverride(null)
  }, [id])
}
